import {
  Alert,
  Box,
  Button,
  Code,
  Group,
  Modal,
  Progress,
  Stack,
  Switch,
  Text,
  TextInput,
  Select,
  Textarea,
} from '@mantine/core';
import { ChipRail, KV, KVRow, KVSection } from '../../ui';
import { tokens as T } from '../../theme/tokens';
import { useUIStore, type ModalKind } from '../../stores/uiStore';
import { useMemoriesStore, usePoolStore } from '../../stores/dataStores';
import { forgetMemory } from '../../api/memory';
import { canForget, memoryTitle } from '../../data/memoryState';
import { getSources } from '../../stores/sources';
import {
  addPoolKey,
  canDelete,
  confirmTarget,
  deletePoolKey,
  isOverflowKey,
  pendingEdits,
  saveKeyEdits,
  type KeyEditOutcome,
} from '../../api/pool';
import { ApiError } from '../../api/client';
import { useConsoleNav, useConsolePrefix } from '../../shell/nav';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';

/* Six dialogs and a five-step wizard behind one chrome, because the design has
 * one chrome: a rail-coloured header, a body, and a footer whose only variation
 * is a Back button and the label on the primary action.
 *
 * Five of them are inert. There is no control-plane write route for those, so
 * the primary action is disabled and says why — a dialog that closes with a
 * success toast and changes nothing would be the worst thing on this screen.
 *
 * Delete is the exception and does not share this chrome: it WRITES, so it owns
 * its own lifecycle below (DeleteKeyModal) rather than borrowing a dispatcher
 * that has no per-modal state to reset. */

/** The one chrome, named once so the dialog that opted out of the shared
 *  dispatcher still looks identical to the ones that did not. */
const CHROME = {
  size: 620,
  radius: 'xl',
  centered: true,
  padding: 0,
  overlayProps: { backgroundOpacity: 0.42, color: T.ink },
  styles: {
    header: { background: T.rail, padding: '18px 22px', minHeight: 'auto' },
    title: { fontSize: 18, fontWeight: 800 },
    body: { padding: 22, maxHeight: '86vh', overflowY: 'auto' },
    close: { color: T.ink3 },
  },
} as const;

const TITLES: Record<ModalKind['kind'], string> = {
  addKey: 'Add pool key',
  editKey: 'Edit pool key',
  deleteKey: 'Delete pool key',
  newEstate: 'New estate',
  newTenant: 'New tenant',
  tenantKey: 'Add tenant provider key',
  mintToken: 'Mint router token',
  forgetMemory: 'Forget memory',
  unavailable: 'Not available yet',
};

const CTAS: Record<ModalKind['kind'], string> = {
  addKey: 'Add key',
  editKey: 'Save',
  deleteKey: 'Delete key',
  newEstate: 'Create estate',
  newTenant: 'Create tenant',
  tenantKey: 'Seal and save',
  mintToken: 'Mint',
  forgetMemory: 'Forget',
  unavailable: 'Close',
};

const WIZARD = ['IDENTITY', 'REALMS', 'PROVIDERS', 'BILLING', 'REVIEW'];

/* What the estate wizard has been told so far.
 *
 * It is real form state, not a script. The review step used to render a
 * hard-coded example — "ac", "Atherada" — no matter what was typed, which put
 * fixture-shaped data on an authed screen (spec/decisions.md DR-24) and, worse,
 * showed an operator a summary that was not of their own input. */
interface EstateDraft {
  id: string;
  displayName: string;
  enabled: boolean;
  realm: string;
  issuer: string;
  audience: string;
  jwksUrl: string;
  tenantClaim: string;
  providers: string[];
  modelDeny: string;
  poolScope: 'shared' | 'reserved';
  lane: 'event_key' | 'composed';
  eventType: string;
  billable: string;
  attributed: string;
  timeout: string;
}

const EMPTY_DRAFT: EstateDraft = {
  id: '',
  displayName: '',
  enabled: false,
  realm: '',
  issuer: '',
  audience: '',
  jwksUrl: '',
  tenantClaim: 'tenant_id',
  providers: [],
  modelDeny: '',
  poolScope: 'shared',
  lane: 'event_key',
  eventType: '',
  billable: '600',
  attributed: '60',
  timeout: '600s',
};

const ALL_PROVIDERS = ['anthropic', 'openai', 'meta', 'kimi-code', 'moonshot', 'deepseek', 'xai', 'gemini', 'elevenlabs'];

/* Gemini is absent from BOTH key lists on purpose: it runs on Vertex ADC and a
 * key written for it is rejected at write, not silently ignored. */
const OAUTH_PROVIDERS = ['anthropic', 'openai', 'meta', 'kimi-code'];
const KEYABLE_PROVIDERS = ['anthropic', 'openai', 'moonshot', 'deepseek', 'xai', 'zai'];
/* The pool also holds a decision model's key (TypeSafe's Jev, served by
 * /v1/decide — DR-83) and ElevenLabs' key, which mints single-use realtime TTS
 * tokens. Pool only: a tenant brings no key for either. */
const POOL_KEYABLE_PROVIDERS = [...KEYABLE_PROVIDERS, 'typesafe', 'elevenlabs'];

const SCOPES = [
  { value: '', label: 'Shared — all estates' },
  { value: 'sw', label: 'Reserved · sw' },
  { value: 'am', label: 'Reserved · am' },
  { value: 'hal', label: 'Reserved · hal' },
];

/** "—" rather than a plausible-looking blank: an empty review row should read as
 *  "you did not set this", not as a value. */
const orDash = (v: string) => (v.trim() === '' ? '—' : v.trim());

export function ConsoleModals() {
  const modal = useUIStore((s) => s.modal);
  const close = useUIStore((s) => s.closeModal);
  const step = useUIStore((s) => s.wizardStep);
  const setUI = useUIStore((s) => s.set);
  const canMutate = getSources().canMutate;
  const [draft, setDraft] = useState<EstateDraft>(EMPTY_DRAFT);

  if (!modal) return null;
  // Delete is the one dialog with a route, so it is the one dialog that needs
  // per-open state — the typed confirmation, an in-flight flag, a server error.
  // Mounting it as its own component keyed on the key id is what makes that
  // state reset when the operator closes it and opens another key's dialog:
  // ConsoleModals itself stays mounted the whole session, so state lifted here
  // would survive every close.
  if (modal.kind === 'deleteKey') {
    return <DeleteKeyModal key={modal.keyId} keyId={modal.keyId} onClose={close} />;
  }
  if (modal.kind === 'editKey') {
    return <EditKeyModal key={modal.keyId} keyId={modal.keyId} onClose={close} />;
  }
  if (modal.kind === 'addKey') {
    return <AddKeyModal onClose={close} />;
  }
  if (modal.kind === 'forgetMemory') {
    return (
      <ForgetMemoryModal
        key={modal.id}
        estateId={modal.estateId}
        tenantId={modal.tenantId}
        id={modal.id}
        onClose={close}
      />
    );
  }
  const isWizard = modal.kind === 'newEstate';
  const title = isWizard ? `New estate — step ${step + 1} of 5` : TITLES[modal.kind];
  const cta = isWizard ? (step < 4 ? 'Continue' : CTAS.newEstate) : CTAS[modal.kind];

  return (
    <Modal opened onClose={close} title={title} {...CHROME}>
      {isWizard ? <WizardRail step={step} onJump={(i) => setUI('wizardStep', i)} /> : null}

      {modal.kind === 'newEstate' ? (
        <EstateWizardStep step={step} draft={draft} setDraft={setDraft} />
      ) : null}
      {modal.kind === 'newTenant' ? <NewTenantBody estateId={modal.estateId} /> : null}
      {modal.kind === 'tenantKey' ? (
        <TenantKeyBody estateId={modal.estateId} tenantId={modal.tenantId} />
      ) : null}
      {modal.kind === 'mintToken' ? <MintTokenBody /> : null}
      {modal.kind === 'unavailable' ? (
        <Text fz={14} lh={1.65} c={T.ink2}>
          <b>{modal.action}</b> {modal.detail}
        </Text>
      ) : null}

      {!canMutate && modal.kind !== 'unavailable' ? (
        <Alert color="orange" mt={18} radius="lg" title="Nothing will be written">
          The control plane has no write route for this yet, so this dialog cannot save. It is here
          so the surface can be reviewed — not so it can pretend.
        </Alert>
      ) : null}

      <Group
        justify="space-between"
        gap={10}
        mt={24}
        pt={18}
        style={{ borderTop: `1px solid ${T.lineSoft}` }}
      >
        {isWizard && step > 0 ? (
          <Button variant="default" onClick={() => setUI('wizardStep', step - 1)}>
            ← Back
          </Button>
        ) : (
          <Box />
        )}
        <Group gap={10}>
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!canMutate && modal.kind !== 'unavailable' && !(isWizard && step < 4)}
            onClick={() => {
              if (isWizard && step < 4) setUI('wizardStep', step + 1);
              else close();
            }}
          >
            {cta}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}

function WizardRail({ step, onJump }: { step: number; onJump: (i: number) => void }) {
  return (
    <Group grow gap={8} mb={22} align="flex-start">
      {WIZARD.map((label, i) => (
        <Box
          key={label}
          onClick={i < step ? () => onJump(i) : undefined}
          style={{ cursor: i < step ? 'pointer' : 'default' }}
        >
          <Progress
            value={100}
            size={4}
            transitionDuration={0}
            color={i < step ? T.coralBorder : i === step ? T.coral : T.border}
          />
          <Group gap={6} mt={8} wrap="nowrap">
            <Text fz={10} fw={800} c={i <= step ? T.coralDeep : T.faint}>
              {i + 1}
            </Text>
            <Text fz={10} fw={700} c={i === step ? T.ink : T.ink5} style={{ letterSpacing: '0.06em' }}>
              {label}
            </Text>
          </Group>
        </Box>
      ))}
    </Group>
  );
}

function Label({ children, mt }: { children: string; mt?: number }) {
  return (
    <Text fz={13} fw={700} mt={mt} mb={7}>
      {children}
    </Text>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <Text fz={12} c={T.ink4} lh={1.55} mt={8}>
      {children}
    </Text>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <Box
      mt={16}
      style={{ background: T.rail, borderRadius: 10, padding: '13px 15px' }}
    >
      <Text fz={12} c={T.ink3} lh={1.6}>
        {children}
      </Text>
    </Box>
  );
}

/** Adding a credential (DR-99). The one other dialog that takes a secret in,
 *  held the way the edit dialog holds one: a plain field with autocomplete
 *  off, state that dies with the dialog, and a response that is the projected
 *  row. An API key for anthropic or openai is OVERFLOW — the router draws it
 *  only when no subscription key is selectable, or for an API a subscription
 *  cannot serve — and the dialog says so before it is saved. */
function AddKeyModal({ onClose }: { onClose: () => void }) {
  const upsert = usePoolStore((s) => s.upsert);
  const harness = useConsolePrefix() === '/dev';
  const live = !harness && getSources().live.kind === 'live';
  const [kind, setKind] = useState<'oauth' | 'api_key'>('oauth');
  const [provider, setProvider] = useState('anthropic');
  const [scope, setScope] = useState('');
  const [label, setLabel] = useState('');
  const [credential, setCredential] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const isOAuth = kind === 'oauth';
  const providers = isOAuth ? OAUTH_PROVIDERS : POOL_KEYABLE_PROVIDERS;
  const overflow = isOverflowKey({ kind, provider });
  const ready = live && !busy && providers.includes(provider) && credential.trim() !== '';

  const dismiss = () => {
    if (!busy) onClose();
  };

  async function save() {
    if (!ready) return;
    setBusy(true);
    setFailed(null);
    try {
      const key = await addPoolKey({ provider, kind, label: label.trim(), scope, credential: credential.trim(), enabled });
      upsert(key.id, key);
      onClose();
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={dismiss} title={TITLES.addKey} closeOnClickOutside={!busy} closeOnEscape={!busy} withCloseButton={!busy} {...CHROME}>
      <Stack gap={0}>
        <Label>Credential kind</Label>
        <ChipRail
          value={kind}
          onChange={(v) => {
            const next = v as 'oauth' | 'api_key';
            setKind(next);
            const list = next === 'oauth' ? OAUTH_PROVIDERS : POOL_KEYABLE_PROVIDERS;
            if (!list.includes(provider)) setProvider(list[0]);
          }}
          fullWidth
          data={[
            { value: 'oauth', label: 'Subscription OAuth' },
            { value: 'api_key', label: 'API key' },
          ]}
        />
        <Group grow gap={14} mt={18} align="flex-start">
          <Select label="Provider" data={providers} value={provider} onChange={(v) => setProvider(v ?? providers[0])} allowDeselect={false} disabled={busy} />
          <Select label="Scope" data={SCOPES} value={scope} onChange={(v) => setScope(v ?? '')} allowDeselect={false} disabled={busy} />
        </Group>
        <TextInput label="Label" placeholder="jesse@binaryblur.com" mt={18} value={label} onChange={(e) => setLabel(e.currentTarget.value)} disabled={busy} />
        <Label mt={18}>{isOAuth ? 'Token-set JSON' : 'API key'}</Label>
        <Note>
          {isOAuth
            ? 'The subscription OAuth token set. Stored as the documented raw-secret exception — server-only, index-exempt, never serialised onto an API surface.'
            : 'Sealed by the router with credvault before it is written. Static keys get no raw-secret exception; only rotating token sets do.'}
        </Note>
        <Textarea
          rows={isOAuth ? 5 : 2}
          mt={9}
          value={credential}
          onChange={(e) => setCredential(e.currentTarget.value)}
          disabled={busy || !live}
          autoComplete="off"
          spellCheck={false}
          placeholder={isOAuth ? '{"access_token":"…","refresh_token":"…","expires_at":…}' : 'sk-… / xai-… / provider key'}
          styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
        />
        {overflow ? (
          <Callout>
            An {provider} API key is <b>overflow</b>: the router draws it only when no {provider} subscription key is
            selectable — every one rested, spent, disabled or gone — or for an API a subscription cannot serve
            (OpenAI embeddings). It bills per token where the subscription is already paid for.
          </Callout>
        ) : null}
        <Group mt={18} gap={10}>
          <Switch checked={enabled} onChange={(e) => setEnabled(e.currentTarget.checked)} disabled={busy} label="Enable on add" />
        </Group>
        <Note>Off is the safe default: add it, run a probe from the key&rsquo;s page, then switch it on.</Note>

        {!live ? (
          <Alert color="orange" mt={16} radius="lg" title="Nothing will be written">
            {harness ? 'This is the preview harness; adding a key here would reach the real pool.' : 'This console is not connected to a live pool.'}
          </Alert>
        ) : null}
        {failed ? (
          <Alert color="red" mt={16} radius="lg" title="Not added">
            {failed}
          </Alert>
        ) : null}
      </Stack>
      <Group justify="flex-end" gap={10} mt={24} pt={18} style={{ borderTop: `1px solid ${T.lineSoft}` }}>
        <Button variant="default" onClick={dismiss} disabled={busy}>
          Cancel
        </Button>
        <Button loading={busy} disabled={!ready} onClick={() => void save()}>
          {CTAS.addKey}
        </Button>
      </Group>
    </Modal>
  );
}

/** Editing a key: its label, and the credential behind it (DR-78).
 *
 *  Two different writes behind one Save, kept apart on the server on purpose:
 *  the rename route accepts a single field and refuses any other, and the
 *  replace route takes the credential and nothing else, so neither can
 *  disturb what the other owns. The dialog sends only what changed — a
 *  blank credential box is not a replacement — and reports a partial outcome
 *  as exactly that (`saveKeyEdits`).
 *
 *  The credential box is the one place in the console a secret is typed. It
 *  is a plain field with autocomplete off, so the browser's form-restore cache
 *  does not keep it; it is never echoed — the response is the projected row;
 *  and the state dies with the dialog, which is mounted per key. The server
 *  decides what the text must be from the stored row's kind, so the box is
 *  labelled by that kind rather than offering a choice. */
function EditKeyModal({ keyId, onClose }: { keyId: string; onClose: () => void }) {
  const poolKey = usePoolStore((s) => s.data.byId[keyId]);
  const upsert = usePoolStore((s) => s.upsert);
  const harness = useConsolePrefix() === '/dev';
  const live = !harness && getSources().live.kind === 'live';
  const originalLabel = poolKey?.label ?? '';
  const [label, setLabel] = useState(originalLabel);
  const [credential, setCredential] = useState('');
  const [busy, setBusy] = useState(false);
  // What the last Save reported — the failure AND what landed before it.
  // Kept from the outcome rather than recomputed: once the renamed row is
  // upserted, the label it opened with IS the new one, and a recomputation
  // would say no rename was pending — in exactly the case the text is for.
  const [failed, setFailed] = useState<(KeyEditOutcome['failed'] & { renamed: boolean; replaceSkipped: boolean }) | null>(null);

  const edits = { label, originalLabel, credential };
  const pending = pendingEdits(edits);
  const changed = pending.rename || pending.replace;
  const isApiKey = poolKey?.kind === 'api_key';

  const dismiss = () => {
    if (!busy) onClose();
  };

  async function save() {
    if (!live || busy) return;
    // A no-op needs no round trip, and reporting "saved" for a request nobody
    // made is the kind of small lie that makes a console untrustworthy.
    if (!changed) {
      onClose();
      return;
    }
    setBusy(true);
    setFailed(null);
    const out = await saveKeyEdits(keyId, edits);
    // Whatever landed is on screen, even when the second write did not: the
    // response IS the updated row, so each write costs one request and
    // touches exactly one row in the table.
    if (out.key) upsert(keyId, out.key);
    if (out.failed) {
      setFailed({
        ...out.failed,
        renamed: out.done.rename,
        replaceSkipped: out.failed.stage === 'rename' && pending.replace,
      });
      setBusy(false);
      return;
    }
    onClose();
  }

  return (
    <Modal
      opened
      onClose={dismiss}
      title={TITLES.editKey}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
      {...CHROME}
    >
      <Stack gap={0}>
        <TextInput
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
          disabled={busy || !live}
          placeholder={keyId}
          styles={{ input: { fontFamily: T.fontMono } }}
        />
        <Note>
          The name only. Leave it empty and the console falls back to the key id.
        </Note>

        <Label mt={18}>{isApiKey ? 'Replace API key' : 'Replace token-set JSON'}</Label>
        <Textarea
          rows={isApiKey ? 2 : 5}
          value={credential}
          onChange={(e) => setCredential(e.currentTarget.value)}
          disabled={busy || !live}
          autoComplete="off"
          spellCheck={false}
          placeholder={
            isApiKey
              ? 'sk-… / xai-… / provider key, or the minter\u2019s {"api_key": …} file'
              : '{"access_token":"…","refresh_token":"…","expires_at":…}'
          }
          styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
        />
        <Note>
          Leave it empty to keep the current credential. Pasting one replaces the stored{' '}
          {isApiKey ? 'key, sealed by the router before it is written' : 'token set'}; the row
          keeps its id, scope, counters and history. The old credential&rsquo;s rest, last error
          and quota snapshot are cleared, and a key the pool turned off for a dead refresh or a
          spent account comes back on &mdash; a key you disabled stays off. A request already in
          flight on the old credential finishes on it, another router instance may still select
          it for up to 20 seconds, and nothing is revoked at the provider.
        </Note>

        {!live ? (
          <Alert color="orange" mt={16} radius="lg" title="Nothing will be written">
            {harness
              ? 'This is the preview harness. Its rows are fixtures, and a rename or a replacement here would reach the real pool.'
              : 'This console is not connected to a live pool.'}
          </Alert>
        ) : null}

        {failed ? (
          <Alert
            color="red"
            mt={16}
            radius="lg"
            title={failed.stage === 'rename' ? 'Not renamed' : 'Credential not replaced'}
          >
            {failed.replaceSkipped
              ? 'The credential was not sent either. '
              : failed.renamed
                ? 'The name was saved. '
                : ''}
            {failed.message}
          </Alert>
        ) : null}
      </Stack>

      <Group
        justify="flex-end"
        gap={10}
        mt={24}
        pt={18}
        style={{ borderTop: `1px solid ${T.lineSoft}` }}
      >
        <Button variant="default" onClick={dismiss} disabled={busy}>
          Cancel
        </Button>
        <Button loading={busy} disabled={!live || !changed} onClick={() => void save()}>
          {CTAS.editKey}
        </Button>
      </Group>
    </Modal>
  );
}

/** The one console dialog that writes.
 *
 *  Everything about it is shaped by the fact that the write cannot be undone. A
 *  subscription's stored payload is the ONLY copy of a refresh-token family that
 *  has already rotated past the JSON it was seeded from, so a wrong delete is
 *  repaired by going back to the provider for fresh consent — not by re-running
 *  add-oauth. Hence: it refuses an enabled key (the server does too, and says
 *  so), it asks for the label typed out, and it reports a failure by staying
 *  open rather than by closing quietly. */
function DeleteKeyModal({ keyId, onClose }: { keyId: string; onClose: () => void }) {
  const poolKey = usePoolStore((s) => s.data.byId[keyId]);
  const load = usePoolStore((s) => s.load);
  const drop = usePoolStore((s) => s.drop);
  const navigate = useConsoleNav();
  const { pathname } = useLocation();
  const harness = useConsolePrefix() === '/dev';
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The arming rule is a pure function so it can be tested without a DOM —
  // "the button never un-grays" is the bug this dialog exists to fix.
  const target = confirmTarget(poolKey?.label, keyId);
  // TWO independent locks on the fixture harness, because one is not enough and
  // the thing they prevent is deleting a real credential from a screen showing
  // sample data. The source kind can flip to live under a page that is STILL
  // rendering fixture rows — sign-in installs the live sources asynchronously —
  // so the id in this dialog would be a fixture's while `live` said yes. The
  // route is the fact that cannot drift: nothing under /dev ever writes.
  const live = !harness && getSources().live.kind === 'live';
  const enabled = poolKey?.enabled ?? false;
  const armed = canDelete({ typed: confirm, target, enabled, live, busy });

  async function run() {
    if (!armed) return;
    setBusy(true);
    setError(null);
    try {
      await deletePoolKey(keyId);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
      setBusy(false);
      return;
    }
    // The credential is gone; what is left is making the screen agree. The row
    // is dropped locally FIRST and the refetch is a reconciliation, not the
    // mechanism: a failed refetch keeps the rows it already has (correctly — a
    // load must never blank a table), which would leave the deleted credential
    // on screen inviting a retry that can only 404.
    drop(keyId);
    load(undefined as void);
    // /pool/:keyId now addresses a credential that does not exist, and would
    // render "Not connected" — which reads as an outage rather than as the
    // deletion the operator just performed.
    if (pathname.includes(`/pool/${encodeURIComponent(keyId)}`)) navigate('/pool');
    onClose();
  }

  // Dismissal is blocked while the request is in flight. Otherwise the operator
  // can dismiss this, open another key's dialog, and have THIS one's completion
  // call onClose() on the new one — closing a dialog whose delete never ran,
  // and navigating away using a pathname captured before any of it.
  const dismiss = () => {
    if (!busy) onClose();
  };

  return (
    <Modal
      opened
      onClose={dismiss}
      title={TITLES.deleteKey}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
      {...CHROME}
    >
      <Stack gap={0}>
        <Text fz={14} lh={1.65} c={T.ink2}>
          Delete <b>{poolKey?.label || keyId}</b> from the pool? Its token material is destroyed —
          this cannot be undone, and re-adding the credential means a fresh consent at the provider.
        </Text>
        <Callout>
          The ledger keeps every usage row this key produced, so historical accounting and billing
          are unaffected. The per-key counters on the pool row — call count, tokens, failures — live
          on the credential itself and go with it. In-flight completions finish; a refresh that
          lands after the delete fails, and other instances may keep selecting it until their
          selection cache expires.
        </Callout>

        {enabled ? (
          <Alert color="orange" mt={16} radius="lg" title="Disable it first">
            <Text fz={13} lh={1.6}>
              This key is enabled and may be serving traffic right now. Because the delete cannot be
              undone, it is refused until the key is disabled — a disable is reversible, and a pool
              that keeps serving without it is the proof that it is the redundant one.
            </Text>
            <Code block mt={10}>{`haus pool disable --id ${keyId} --project agentic-router-prod`}</Code>
          </Alert>
        ) : null}

        {!live ? (
          <Alert color="orange" mt={16} radius="lg" title="Nothing will be written">
            {harness
              ? 'This is the preview harness. Its rows are fixtures, and a delete here would reach the real pool — so nothing under /dev writes, ever.'
              : 'This console is not connected to a live pool, so there is nothing to delete.'}
          </Alert>
        ) : null}

        <Label mt={18}>Type the label to confirm</Label>
        <TextInput
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          placeholder={target}
          disabled={busy || enabled || !live}
          styles={{ input: { fontFamily: T.fontMono } }}
        />

        {error ? (
          <Alert color="red" mt={16} radius="lg" title="Not deleted">
            {error}
          </Alert>
        ) : null}
      </Stack>

      <Group
        justify="flex-end"
        gap={10}
        mt={24}
        pt={18}
        style={{ borderTop: `1px solid ${T.lineSoft}` }}
      >
        <Button variant="default" onClick={dismiss} disabled={busy}>
          Cancel
        </Button>
        <Button color="red" loading={busy} disabled={!armed} onClick={() => void run()}>
          {CTAS.deleteKey}
        </Button>
      </Group>
    </Modal>
  );
}

/** ForgetMemoryModal tombstones one memory — the one mutation a browser may
 *  perform on the memory store (DR-75). Same shape as DeleteKeyModal: its
 *  own state per open, both locks on the harness, the row refreshed in place
 *  from the server's answer. The confirmation target is the memory's ID,
 *  never its text: the text is agent-written tenant content. */
function ForgetMemoryModal({
  estateId,
  tenantId,
  id,
  onClose,
}: {
  estateId: string;
  tenantId: string;
  id: string;
  onClose: () => void;
}) {
  const memory = useMemoriesStore((s) => s.data.byId[id]);
  const upsert = useMemoriesStore((s) => s.upsert);
  const harness = useConsolePrefix() === '/dev';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = !harness && getSources().live.kind === 'live';
  const armed = canForget(memory, live, busy);

  async function run() {
    if (!armed) return;
    setBusy(true);
    setError(null);
    let after;
    try {
      after = await forgetMemory(estateId, tenantId, id);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
      setBusy(false);
      return;
    }
    // The row flips to "deleted" in place from the server's own answer; a
    // status filter that excludes tombstones drops it on the next load.
    upsert(id, after);
    onClose();
  }

  const dismiss = () => {
    if (!busy) onClose();
  };

  return (
    <Modal
      opened
      onClose={dismiss}
      title={TITLES.forgetMemory}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
      {...CHROME}
    >
      <Stack gap={0}>
        <Text fz={14} lh={1.65} c={T.ink2}>
          Forget memory <Code>{id}</Code>? It becomes a tombstone: hidden from every recall at
          once, removed from the search index within about a minute, and hard-purged thirty days
          after the index confirms the removal. Re-remembering the same statement revives it.
        </Text>
        {memory ? (
          <Callout>
            <Text fz={13} c={T.ink3} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {memoryTitle(memory, 240)}
            </Text>
          </Callout>
        ) : null}
        {memory && memory.status !== 'active' ? (
          <Alert color="orange" mt={16} radius="lg" title="Already not active">
            This memory is {memory.status}; only an active memory can be forgotten from here.
          </Alert>
        ) : null}
        {!live ? (
          <Alert color="orange" mt={16} radius="lg" title="Nothing will be written">
            {harness
              ? 'This is the preview harness. Its rows are fixtures, and a forget here would reach the real store — so nothing under /dev writes, ever.'
              : 'This console is not connected to a live memory store, so there is nothing to forget.'}
          </Alert>
        ) : null}
        {error ? (
          <Alert color="red" mt={16} radius="lg" title="Not forgotten">
            {error}
          </Alert>
        ) : null}
      </Stack>
      <Group justify="flex-end" gap={10} mt={24} pt={18} style={{ borderTop: `1px solid ${T.lineSoft}` }}>
        <Button variant="default" onClick={dismiss} disabled={busy}>
          Cancel
        </Button>
        <Button color="red" loading={busy} disabled={!armed} onClick={() => void run()}>
          {CTAS.forgetMemory}
        </Button>
      </Group>
    </Modal>
  );
}

function EstateWizardStep({
  step,
  draft,
  setDraft,
}: {
  step: number;
  draft: EstateDraft;
  setDraft: (d: EstateDraft) => void;
}) {
  const patch = <K extends keyof EstateDraft>(k: K, v: EstateDraft[K]) =>
    setDraft({ ...draft, [k]: v });

  if (step === 0) {
    return (
      <Stack gap={0}>
        <Group grow gap={14} align="flex-start">
          <TextInput
            label="Estate id"
            placeholder="ac"
            value={draft.id}
            onChange={(e) => patch('id', e.currentTarget.value)}
            styles={{ input: { fontFamily: T.fontMono } }}
          />
          <TextInput
            label="Display name"
            placeholder="Atherada"
            value={draft.displayName}
            onChange={(e) => patch('displayName', e.currentTarget.value)}
          />
        </Group>
        <Note>
          The id is credential-derived at request time and never caller-supplied — it becomes part of
          every dedup tuple and pool-scope value, so it cannot be renamed later. Short, lowercase, no
          separator characters.
        </Note>
        <Label mt={18}>Start state</Label>
        <ChipRail
          value={draft.enabled ? 'enabled' : 'disabled'}
          onChange={(v) => patch('enabled', v === 'enabled')}
          fullWidth
          data={[
            { value: 'disabled', label: 'Disabled — configure first' },
            { value: 'enabled', label: 'Enabled immediately' },
          ]}
        />
      </Stack>
    );
  }
  if (step === 1) {
    return (
      <Stack gap={0}>
        <Text fz={13} c={T.ink3} lh={1.6} mb={16}>
          One realm is one (issuer, audience, key source) trust triple. The verifier accepts exactly
          one audience per realm — a second audience means a second realm, never a widened list.
        </Text>
        <Group grow gap={14} align="flex-start">
          <TextInput
            label="Realm name"
            placeholder="native"
            value={draft.realm}
            onChange={(e) => patch('realm', e.currentTarget.value)}
            styles={{ input: { fontFamily: T.fontMono } }}
          />
          <TextInput
            label="Tenant claim"
            value={draft.tenantClaim}
            onChange={(e) => patch('tenantClaim', e.currentTarget.value)}
            styles={{ input: { fontFamily: T.fontMono } }}
          />
        </Group>
        <Group grow gap={14} mt={14} align="flex-start">
          <TextInput
            label="Issuer"
            placeholder="https://api.atherada.com"
            value={draft.issuer}
            onChange={(e) => patch('issuer', e.currentTarget.value)}
            styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
          />
          <TextInput
            label="Audience"
            placeholder="https://llm.atherada.com"
            value={draft.audience}
            onChange={(e) => patch('audience', e.currentTarget.value)}
            styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
          />
        </Group>
        <TextInput
          label="JWKS URL"
          mt={14}
          placeholder="https://api.atherada.com/.well-known/jwks.json"
          value={draft.jwksUrl}
          onChange={(e) => patch('jwksUrl', e.currentTarget.value)}
          styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
        />
        <Callout>
          A native realm mints through this control plane and carries{' '}
          <Text component="span" ff="monospace">
            estate_id
          </Text>
          ; a migrated realm keeps the caller&apos;s existing issuer so no live token must be
          re-minted. Set RetireAfter only once the realm&apos;s traffic metric reads zero.
        </Callout>
      </Stack>
    );
  }
  if (step === 2) {
    return (
      <Stack gap={0}>
        <Label>Allowed providers</Label>
        <Group gap={8} wrap="wrap">
          {ALL_PROVIDERS.map((p) => {
            const on = draft.providers.includes(p);
            return (
              <Group
                key={p}
                gap={7}
                onClick={() =>
                  patch(
                    'providers',
                    on ? draft.providers.filter((x) => x !== p) : [...draft.providers, p],
                  )
                }
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '7px 13px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: `1px solid ${on ? T.coralBorder : T.border}`,
                  background: on ? T.coralSoft : T.card,
                  color: on ? T.coralDeep : T.ink4,
                }}
              >
                <Text component="span" fz={11}>
                  {on ? '✓' : '+'}
                </Text>
                {p}
              </Group>
            );
          })}
        </Group>
        <Note>Gemini runs on Vertex ADC only — selecting it grants no key rung.</Note>
        <TextInput
          label="Model deny-list"
          mt={18}
          placeholder="claude-opus-4-6, gpt-5.2-pro"
          value={draft.modelDeny}
          onChange={(e) => patch('modelDeny', e.currentTarget.value)}
        />
        <Label mt={18}>Pool scope</Label>
        <ChipRail
          value={draft.poolScope}
          onChange={(v) => patch('poolScope', v as EstateDraft['poolScope'])}
          fullWidth
          data={[
            { value: 'shared', label: 'Shared keys only' },
            { value: 'reserved', label: 'Shared + reserved' },
          ]}
        />
        <Note>
          Reserve keys only under contract or a demonstrated noisy-neighbour problem — sharing is the
          economic argument for one router. The per-estate rate limit is the counterweight that stops
          one estate draining the pool.
        </Note>
      </Stack>
    );
  }
  if (step === 3) {
    return (
      <Stack gap={0}>
        <Label>Billing envelope lane</Label>
        <ChipRail
          value={draft.lane}
          onChange={(v) => patch('lane', v as EstateDraft['lane'])}
          fullWidth
          data={[
            { value: 'event_key', label: 'event_key' },
            { value: 'composed', label: 'composed' },
          ]}
        />
        <Note>
          The lane is pinned here: an envelope from the other lane is rejected as an invalid request
          before the provider call.
        </Note>
        <TextInput
          label="Collector event type"
          mt={18}
          placeholder="llm_usage"
          value={draft.eventType}
          onChange={(e) => patch('eventType', e.currentTarget.value)}
          styles={{ input: { fontFamily: T.fontMono, fontSize: 13 } }}
        />
        <Note>
          The event type is what the collector routes on — a record stamped with this estate can only
          reach that pipeline.
        </Note>
        <Group grow gap={14} mt={18} align="flex-start">
          <TextInput
            label="Billable / min"
            value={draft.billable}
            onChange={(e) => patch('billable', e.currentTarget.value)}
          />
          <TextInput
            label="Attributed / min"
            value={draft.attributed}
            onChange={(e) => patch('attributed', e.currentTarget.value)}
          />
          <TextInput
            label="Timeout"
            value={draft.timeout}
            onChange={(e) => patch('timeout', e.currentTarget.value)}
          />
        </Group>
      </Stack>
    );
  }
  return (
    <Stack gap={0}>
      <Text fz={13} c={T.ink3} lh={1.6} mb={16}>
        Creating the estate writes{' '}
        <Text component="span" ff="monospace">
          router_estates
        </Text>{' '}
        and an audit row. Nothing routes until the realm&apos;s JWKS is reachable and the estate is
        enabled.
      </Text>
      <KVSection title="Review">
        <KVRow>
          <KV k="Estate id" v={orDash(draft.id)} mono />
        </KVRow>
        <KVRow>
          <KV k="Display name" v={orDash(draft.displayName)} />
        </KVRow>
        <KVRow>
          <KV
            k="Realm"
            v={
              draft.realm || draft.audience
                ? `${orDash(draft.realm)} · ${orDash(draft.audience)}`
                : '—'
            }
          />
        </KVRow>
        <KVRow>
          <KV k="Providers" v={draft.providers.length ? draft.providers.join(', ') : '—'} />
        </KVRow>
        <KVRow>
          <KV
            k="Pool scope"
            v={draft.poolScope === 'shared' ? 'shared keys only' : 'shared + reserved'}
          />
        </KVRow>
        <KVRow>
          <KV k="Envelope lane" v={draft.lane} />
        </KVRow>
        <KVRow>
          <KV
            k="Rate limits"
            v={`${orDash(draft.billable)} billable · ${orDash(draft.attributed)} attributed / min · ${orDash(draft.timeout)}`}
          />
        </KVRow>
        <KVRow>
          <KV
            k="State on create"
            v={draft.enabled ? 'enabled' : 'disabled'}
            fg={draft.enabled ? T.okFg : T.coralDeep}
          />
        </KVRow>
      </KVSection>
      <Callout>
        Post-create checklist: grant the collector service account{' '}
        <Text component="span" ff="monospace">
          roles/pubsub.publisher
        </Text>{' '}
        on the route, verify the JWKS fetch, mint one attributed token and call{' '}
        <Text component="span" ff="monospace">
          GET /v1/identity
        </Text>{' '}
        to confirm the resolved estate before enabling.
      </Callout>
    </Stack>
  );
}

function NewTenantBody({ estateId }: { estateId: string }) {
  return (
    <Stack gap={0}>
      <Group grow gap={14} align="flex-start">
        <TextInput label="Estate" readOnly value={estateId} />
        <TextInput label="Tenant id" placeholder="acme-industrial" styles={{ input: { fontFamily: T.fontMono } }} />
      </Group>
      <TextInput label="Display name" placeholder="Acme Industrial" mt={18} />
      <Label mt={18}>Default billing mode</Label>
      <ChipRail
        value="attributed"
        onChange={() => {}}
        fullWidth
        data={[
          { value: 'billable', label: 'Billable' },
          { value: 'attributed', label: 'Attributed' },
        ]}
      />
      <Note>
        Attributed is the safe default — usage is recorded and never charged until an operator
        promotes the deployment. Note that a token record with an EMPTY billing mode reads as
        billable, which is why the default applies at mint rather than at read.
      </Note>
    </Stack>
  );
}

function TenantKeyBody({ estateId, tenantId }: { estateId: string; tenantId: string }) {
  return (
    <Stack gap={0}>
      <Select
        label="Provider"
        data={KEYABLE_PROVIDERS}
        defaultValue="anthropic"
        allowDeselect={false}
      />
      <Note>Keyable providers only. Gemini is rejected at write — Vertex ADC is its only rung.</Note>
      <TextInput
        label="API key"
        type="password"
        mt={18}
        placeholder="sk-ant-api03-…"
        styles={{ input: { fontFamily: T.fontMono } }}
      />
      <Callout>
        Sealed with credvault under this tenant's DEK before it is written; the document id is{' '}
        <Text component="span" ff="monospace">
          {estateId}_{tenantId}_anthropic
        </Text>
        . This console will never read the value back.
      </Callout>
    </Stack>
  );
}

function MintTokenBody() {
  return (
    <Stack gap={0}>
      <Group grow gap={14} align="flex-start">
        <TextInput
          label="Deployment"
          readOnly
          value=""
          placeholder="no deployment list to choose from yet"
        />
        <TextInput label="TTL (days)" defaultValue="30" />
      </Group>
      <Label mt={18}>Billing mode</Label>
      <ChipRail
        value="attributed"
        onChange={() => {}}
        fullWidth
        data={[
          { value: 'billable', label: 'Billable' },
          { value: 'attributed', label: 'Attributed' },
        ]}
      />
      <Callout>
        The token is shown once on mint and stored only as a SHA-256 fingerprint, which is compared
        on every verification. TTL is hard-capped at 3650 days. Rotate by minting, distributing, then
        revoking the old — no router restart.
      </Callout>
    </Stack>
  );
}
